import { useState } from "react";
import { formatInTimeZone } from "date-fns-tz";
import { useNavigate } from "react-router-dom";
import { Loading, Panel, Skeleton, Typography } from "@/components/atoms";
import { DataTable, Pagination } from "@/components/molecules";
import { DataTableColumnDef } from "@/components/molecules";
import { PageHeader } from "@/components/organisms/PageHeader/PageHeader";
import { useProfile } from "@/hooks";
import { useGetDriverActivityLogQuery } from "@/redux/apis/driver/driverApi";
import { ActivityLogEvent } from "@/redux/apis/driver/types";
import { capitalize, parseHtml } from "@/utils";
import { DriverNavigation } from "../../common/DriverNavigation";
import { ProfileProvider, useProfileContext } from "../Profile/context";

const getDriverHistoryEventText = (eventType: string, eventName: string, fieldName?: string) => {
  const formatName = (name?: string) => (name ? capitalize(name.replace(/_/g, " ").replace(/-/g, " ").replace(/\./g, " ")) : "");

  switch (eventName) {
    case "driver.status.change":
      return "Status Changed";
    case "driver.account.auto_declined":
      return "Auto Declined";
    case "driver.account.attached_to_provider":
      return "Attached to Provider";
    case "driver.registered":
      return "Driver Registered";
    case "driver.update.first_name":
    case "driver.update.last_name":
    case "driver.update.mobile":
    case "driver.update.email":
    case "driver.update.address":
    case "driver.update.date_of_birth":
    case "driver.update.preferred_name":
      return `Updated ${formatName(fieldName)}`;
    case "driver.fleet_leader.changed":
      return "Fleet Leader Changed";
    case "driver.document.added":
      return "Document Added";
    case "driver.document.removed":
      return "Document Removed";
    case "driver.incident.added":
      return "Incident Report Added";
    case "driver.invitation.sent":
      return "Invitation Sent";
    case "driver.invitation.accepted":
      return "Invitation Accepted";
    default:
      return formatName(fieldName || eventName || eventType);
  }
};

const getDriverHistoryActionText = (name: string, from?: string, to?: string) => {
  const render = (value: string) => {
    return <span className="inline">{parseHtml(value, false)}</span>;
  };

  return (
    <div className="flex max-w-[270px] flex-col break-all">
      {to !== undefined && (
        <Typography variant="small-regular" className="">
          <span className="inline-block min-w-[50px]">To:</span> {to ? render(to) : <span className="text-neutral-dark-gray">Empty</span>}
        </Typography>
      )}
      {from !== undefined && (
        <Typography variant="small-regular" className="">
          <span className="inline-block min-w-[50px]">From:</span>{" "}
          {from ? render(from) : <span className="text-neutral-dark-gray">Empty</span>}
        </Typography>
      )}
    </div>
  );
};

type TableRow = {
  id: string;
  dateTime: JSX.Element;
  modifier: JSX.Element;
  event: JSX.Element;
  details: JSX.Element | null;
};

const columns: DataTableColumnDef<TableRow>[] = [
  {
    header: "Date and Time",
    cell: (props) => props.row.original.dateTime,
    size: 120,
  },
  {
    header: "Modifier",
    cell: (props) => props.row.original.modifier,
    size: 120,
  },
  {
    header: "Event",
    cell: (props) => props.row.original.event,
    size: 200,
  },
  {
    header: "Details",
    cell: (props) => props.row.original.details,
  },
];

export const HistoryLogsPanel = () => {
  const navigate = useNavigate();

  return (
    <ProfileProvider>
      <PageHeader.Actions onBack={() => navigate("../../")} />
      <HistoryLogsContent />
    </ProfileProvider>
  );
};

const DEFAULT_PAGE_SIZE = 25;

const HistoryLogsContent = () => {
  const { driver } = useProfileContext();
  const { dateFormat, fullTimeFormat } = useProfile();
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const { data: response, isFetching } = useGetDriverActivityLogQuery({
    driverUuid: driver.uuid,
    page,
    per_page: pageSize,
  });

  const rows: TableRow[] = (response?._embedded.data ?? []).map((event: ActivityLogEvent, idx: number) => ({
    id: idx.toString(),
    dateTime: (
      <div className="grid">
        <span className="leading-snug">
          {formatInTimeZone(new Date(event.event_time.timestamp), event.event_time.timezone, dateFormat)}
        </span>
        <span className="leading-snug text-neutral-dark-gray">
          {formatInTimeZone(new Date(event.event_time.timestamp), event.event_time.timezone, fullTimeFormat)}
        </span>
      </div>
    ),
    modifier:
      event.event.type !== "notification" && event.performed_by ? (
        <span className="leading-none">
          {event.performed_by.first_name} {event.performed_by.last_name}
        </span>
      ) : (
        <span className="leading-none">System</span>
      ),
    event: (
      <div className="flex items-center gap-2">
        <Typography variant="small-regular" className="truncate leading-snug">
          {getDriverHistoryEventText(event.event.type, event.event.name, event.field?.name)}
        </Typography>
      </div>
    ),
    details: event.field ? getDriverHistoryActionText(event.field.name, event.field.from, event.field.to) : null,
  }));

  const totalItems = response?.total_items ?? 0;
  const pageCount = response?.page_count ?? 0;

  return (
    <div className="flex gap-3">
      <DriverNavigation driver={driver} />
      <Panel>
        <Typography variant="h3">History Logs</Typography>
        <div className="relative mt-6">
          {isFetching && !response && <Skeleton />}
          {isFetching && response && <Loading className="rounded-lg" />}
          {!isFetching && rows.length === 0 ? (
            <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
              No history logs found.
            </Typography>
          ) : (
            <DataTable
              data={rows}
              columns={columns}
              className="w-full overflow-x-auto [&_table]:table-auto [&_tr]:bg-white"
              emptyState={
                <Typography variant="paragraph" className="py-8 text-center text-neutral-dark-gray">
                  No history logs found.
                </Typography>
              }
            />
          )}
        </div>
        {totalItems > 0 && (
          <Pagination
            onPageClick={(p) => setPage(p)}
            onSizeChange={(size) => {
              setPageSize(size);
              setPage(1);
            }}
            currentPage={page}
            pageSize={pageSize}
            totalItems={totalItems}
            pageCount={pageCount}
          />
        )}
      </Panel>
    </div>
  );
};
